import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ShieldCheckIcon } from 'lucide-react';
interface VerificationScreenProps {
  onBack: () => void;
  onContinue: () => void;
}
type TabType = 'account' | 'card';
type AccountOption = 'account_number' | 'wallet';
type CardOption = 'credit_card' | 'prepaid_card';
export function VerificationScreen({
  onBack,
  onContinue
}: VerificationScreenProps) {
  const [activeTab, setActiveTab] = useState<TabType>('account');
  const [selectedAccountOption, setSelectedAccountOption] = useState<AccountOption | null>(null);
  const [selectedCardOption, setSelectedCardOption] = useState<CardOption | null>(null);
  const [inputValue, setInputValue] = useState('');
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setInputValue('');
    // Keep the selected options, just switch view
  };
  const handleAccountOptionSelect = (option: AccountOption) => {
    setSelectedAccountOption(option);
    setSelectedCardOption(null); // Only 1 of 4 can be selected
    setInputValue('');
  };
  const handleCardOptionSelect = (option: CardOption) => {
    setSelectedCardOption(option);
    setSelectedAccountOption(null); // Only 1 of 4 can be selected
    setInputValue('');
  };
  const isContinueEnabled = (selectedAccountOption !== null || selectedCardOption !== null) && inputValue.length > 5;
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full min-h-screen flex flex-col py-6 px-6" style={{
    backgroundColor: '#FFF5F0'
  }} data-id="element-1066">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-1067">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-black/5 transition-colors" data-id="element-1068">
          <ArrowLeftIcon className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2} data-id="element-1069" />
        </button>
        <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-8 w-auto" data-id="element-1070" />
        <div className="w-10" data-id="element-1071" />
      </div>

      {/* Title */}
      <div className="mb-8" data-id="element-1072">
        <div className="w-12 h-12 rounded-full bg-[#FCECE3] flex items-center justify-center mb-4" data-id="element-1073">
          <ShieldCheckIcon className="w-6 h-6 text-[#D95A1A]" strokeWidth={2} data-id="element-1074" />
        </div>
        <h1 className="text-[32px] font-bold text-[#1A1A1A] leading-tight mb-1 tracking-tight" data-id="element-1075">
          Verification
        </h1>
        <p className="text-[15px] text-[#C85A1A]" data-id="element-1076">
          Choose a method to verify your identity
        </p>
      </div>

      {/* Tabs */}
      <div className="flex bg-[#FCECE3] rounded-full p-1 mb-8" data-id="element-1077">
        <button onClick={() => handleTabChange('account')} className={`flex-1 py-3 rounded-full text-sm font-bold transition-all ${activeTab === 'account' ? 'bg-[#D95A1A] text-white shadow-sm' : 'text-[#8A7B72]'}`} data-id="element-1078">
          Account
        </button>
        <button onClick={() => handleTabChange('card')} className={`flex-1 py-3 rounded-full text-sm font-bold transition-all ${activeTab === 'card' ? 'bg-[#D95A1A] text-white shadow-sm' : 'text-[#8A7B72]'}`} data-id="element-1079">
          Card
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 space-y-6" data-id="element-1080">
        {activeTab === 'account' ? <div className="space-y-4" data-id="element-1081">
            <label className={`flex items-center p-4 rounded-2xl border-2 transition-all cursor-pointer ${selectedAccountOption === 'account_number' ? 'border-[#D95A1A] bg-[#FCECE3]' : 'border-transparent bg-white'}`} data-id="element-1082">
              <input type="radio" name="verification_method" checked={selectedAccountOption === 'account_number'} onChange={() => handleAccountOptionSelect('account_number')} className="w-5 h-5 accent-[#D95A1A] mr-4" data-id="element-1083" />
              <span className="text-[16px] font-bold text-[#1A1A1A]" data-id="element-1084">
                Account Number
              </span>
            </label>

            <label className={`flex items-center p-4 rounded-2xl border-2 transition-all cursor-pointer ${selectedAccountOption === 'wallet' ? 'border-[#D95A1A] bg-[#FCECE3]' : 'border-transparent bg-white'}`} data-id="element-1085">
              <input type="radio" name="verification_method" checked={selectedAccountOption === 'wallet'} onChange={() => handleAccountOptionSelect('wallet')} className="w-5 h-5 accent-[#D95A1A] mr-4" data-id="element-1086" />
              <span className="text-[16px] font-bold text-[#1A1A1A]" data-id="element-1087">
                Wallet
              </span>
            </label>

            {selectedAccountOption && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="pt-4" data-id="element-1088">
                <label className="block text-xs font-bold text-[#4A3B32] uppercase tracking-wider mb-2 ml-1" data-id="element-1089">
                  Enter{' '}
                  {selectedAccountOption === 'account_number' ? 'Account Number' : 'Wallet Number'}
                </label>
                <div className="bg-white rounded-2xl px-5 py-4 border border-[#FCECE3]" data-id="element-1090">
                  <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value.replace(/\D/g, ''))} placeholder={`Enter your ${selectedAccountOption === 'account_number' ? 'account' : 'wallet'} number`} className="w-full outline-none text-[15px] text-[#4A3B32] bg-transparent placeholder:text-[#A89F98]" data-id="element-1091" />
                </div>
              </motion.div>}
          </div> : <div className="space-y-4" data-id="element-1092">
            <label className={`flex items-center p-4 rounded-2xl border-2 transition-all cursor-pointer ${selectedCardOption === 'credit_card' ? 'border-[#D95A1A] bg-[#FCECE3]' : 'border-transparent bg-white'}`} data-id="element-1093">
              <input type="radio" name="verification_method" checked={selectedCardOption === 'credit_card'} onChange={() => handleCardOptionSelect('credit_card')} className="w-5 h-5 accent-[#D95A1A] mr-4" data-id="element-1094" />
              <span className="text-[16px] font-bold text-[#1A1A1A]" data-id="element-1095">
                Credit Card
              </span>
            </label>

            <label className={`flex items-center p-4 rounded-2xl border-2 transition-all cursor-pointer ${selectedCardOption === 'prepaid_card' ? 'border-[#D95A1A] bg-[#FCECE3]' : 'border-transparent bg-white'}`} data-id="element-1096">
              <input type="radio" name="verification_method" checked={selectedCardOption === 'prepaid_card'} onChange={() => handleCardOptionSelect('prepaid_card')} className="w-5 h-5 accent-[#D95A1A] mr-4" data-id="element-1097" />
              <span className="text-[16px] font-bold text-[#1A1A1A]" data-id="element-1098">
                Prepaid Card
              </span>
            </label>

            {selectedCardOption && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="pt-4" data-id="element-1099">
                <label className="block text-xs font-bold text-[#4A3B32] uppercase tracking-wider mb-2 ml-1" data-id="element-1100">
                  Enter{' '}
                  {selectedCardOption === 'credit_card' ? 'Credit Card' : 'Prepaid Card'}{' '}
                  Number
                </label>
                <div className="bg-white rounded-2xl px-5 py-4 border border-[#FCECE3]" data-id="element-1101">
                  <input type="text" value={inputValue} onChange={e => {
              const digits = e.target.value.replace(/\D/g, '');
              const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
              setInputValue(formatted.slice(0, 19));
            }} placeholder="XXXX XXXX XXXX XXXX" className="w-full outline-none text-[15px] text-[#4A3B32] bg-transparent placeholder:text-[#A89F98]" data-id="element-1102" />
                </div>
              </motion.div>}
          </div>}
      </div>

      {/* Bottom Section */}
      <div className="pb-4 pt-8" data-id="element-1103">
        <button onClick={onContinue} disabled={!isContinueEnabled} className="w-full bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white py-4 rounded-full font-bold text-[15px] tracking-wide shadow-[0_8px_20px_rgba(217,90,26,0.3)] hover:shadow-[0_8px_25px_rgba(217,90,26,0.4)] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1104">
          SEND OTP
        </button>
      </div>
    </motion.div>;
}